import { Building2, Factory, Sparkles, ArrowUpRight } from "lucide-react";

const audiences = [
  {
    id: "sparkassen-banken",
    icon: Building2,
    label: "Sparkassen & Banken",
    headline: "Spezialisten näher an den Firmenkunden­berater bringen.",
    body: "Firmen­kunden­berater in Sparkassen und Banken arbeiten generalistisch. Sie bieten ein komplexes Produkt­portfolio an — der Firmen­kunden­berater hat mehr als 25 Produkte im Angebot. Diese Komplexität ist für eine Person in der Tiefe neben dem Tagesgeschäft nur schwerlich beherrschbar. Mittel­ständische Kunden setzen in ihrem Unternehmen in der Regel mehr als 12 Produkte ein. Ein Hebel zur nachhaltigen Steigerung der Cross-Selling-Quote liegt darin, interne und externe Spezialisten konsequent in das Kunden­konzept zu integrieren.",
    bullets: [
      "Ideen für die Einbindung interner & externer Spezialisten entwickeln",
      "Tandem-Gespräche: vom Generalisten zur Spezialisten-Lösung",
      "Vorgehensmodelle für nachhaltige Cross-Selling-Erfolge",
      "Coaching-Begleitung für Firmenkunden­berater im realen Termin",
    ],
    kicker: "01 — Banken",
  },
  {
    id: "mittelstand-vendor",
    icon: Factory,
    label: "Mittelstand · Hersteller mobiler Assets",
    headline: "Absatz­finanzierung als Vertriebs­motor.",
    body: "Hersteller und Vertriebe mobiler Wirtschafts­güter — NFZ, Bau­maschinen, Land­technik, IT, Maschinen­bau — gewinnen entscheidende Wett­bewerbs­vorteile, wenn sie ihren Kunden eigene Absatz­finanzierungs­lösungen anbieten. Aus über drei Jahrzehnten Vertriebs­leitung im Leasing weiß ich, welche Bausteine ein erfolgreiches Vendor-Programm wirklich braucht — und welche Stolpersteine zu vermeiden sind.",
    bullets: [
      "Konzeption & Aufbau eigener Absatz­finanzierungs-Programme",
      "Make-or-buy: Partner-Modelle mit Leasing­gesellschaften",
      "Vertriebs­integration: Finanzierung als Verkaufs­argument",
      "Prozesse für automatisierte Kredit­entscheidungen",
    ],
    kicker: "02 — Industrie",
  },
  {
    id: "talente",
    icon: Sparkles,
    label: "Leasing & Sparkassen",
    headline: "Rohdiamanten weiterentwickeln.",
    body: "Junge Führungs­kräfte, ambitionierte Vertriebs­mitarbeiter und Key-Accounter — die personellen Rohdiamanten in Leasing­gesellschaften und Sparkassen — entscheiden über die Schlag­kraft der nächsten Jahre. Ich begleite sie individuell auf dem Weg vom fachlich starken Allrounder zur souveränen Führungs- und Vertriebs­persönlichkeit.",
    bullets: [
      "Coaching für junge Führungs­kräfte: Rolle, Haltung, Wirkung",
      "Entwicklung von Vertriebs­mitarbeitern: Akquise, Abschluss, Bindung",
      "Key-Account-Begleitung: komplexe Kunden professionell führen",
    ],
    kicker: "03 — Talente",
  },
];

export default function Audiences() {
  return (
    <section
      id="zielgruppen"
      data-testid="audiences-section"
      className="section-padding bg-white border-b border-black/10"
    >
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-12 md:mb-20 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Zielgruppen</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Drei klare
              <br />
              <span className="text-[#D30000]">Schwerpunkte</span> —
              <br />
              ein gemeinsamer Hebel.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A52]">
              Aus 30 Jahren Vertriebs­leitung in der Sparkassen-Finanzgruppe
              und im Leasing kenne ich beide Seiten des Tisches: das Haus, das
              Cross-Selling steigern will — und das Unternehmen, das
              Investitions­güter über Finanzierungs­lösungen absetzen möchte.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-black/10 border border-black/10">
          {audiences.map((a) => (
            <article
              key={a.id}
              data-testid={`audience-card-${a.id}`}
              className="bg-white p-8 md:p-12 flex flex-col group transition-colors duration-200 hover:bg-[#0A0A0A] hover:text-white"
            >
              <div className="flex items-center justify-between mb-10">
                <div className="w-14 h-14 border border-current flex items-center justify-center">
                  <a.icon size={22} strokeWidth={1.5} />
                </div>
                <span className="text-xs font-mono opacity-50">{a.kicker}</span>
              </div>

              <div className="text-xs uppercase tracking-[0.2em] opacity-70 mb-4">
                {a.label}
              </div>

              <h3 className="font-display font-bold text-2xl md:text-[32px] tracking-tight leading-[1.1]">
                {a.headline}
              </h3>

              <p className="mt-6 text-base leading-relaxed opacity-80 max-w-prose">
                {a.body}
              </p>

              <ul className="mt-8 space-y-3">
                {a.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-sm md:text-base leading-relaxed"
                  >
                    <span className="mt-2 w-3 h-[2px] bg-[#D30000] shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>

              <a
                href="#kontakt"
                className="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] hover-marker w-fit"
                data-testid={`audience-cta-${a.id}`}
              >
                Gespräch zu diesem Schwerpunkt <ArrowUpRight size={16} />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
